import React, {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import InputFormattedNumber from "../../../components/common/form/input-formatted-number";
import {normalizeNumericValue} from "./document-body-utils";

const formatDisplayValue = (rawValue, decimalScale) => {
    if (rawValue === null || rawValue === undefined || rawValue === "") {
        return "";
    }
    const numeric = Number(rawValue);
    if (Number.isNaN(numeric)) {
        return String(rawValue);
    }
    if (typeof decimalScale === "number") {
        return numeric.toLocaleString(undefined, {
            minimumFractionDigits: decimalScale,
            maximumFractionDigits: decimalScale,
        });
    }
    return numeric.toLocaleString();
};

const ReadOnlyFormattedNumber = ({value, suffix, decimalScale, className}) => {
    const normalized = normalizeNumericValue(value);
    const displayValue = formatDisplayValue(normalized, decimalScale);
    const resolvedClassName = className ? ` ${className}` : "";
    return (
        <span className={`block truncate text-sm text-gray-900${resolvedClassName}`}>
            {displayValue}
            {displayValue && suffix ? ` ${suffix.trim()}` : ""}
        </span>
    );
};

const EditableFormattedNumber = ({
    value,
    onCommit,
    suffix,
    decimalScale,
    allowNegative,
    className,
}) => {
    const initialValue = useRef(normalizeNumericValue(value));
    const [internalValue, setInternalValue] = useState(initialValue.current);
    const isEditingRef = useRef(false);
    const inputRef = useRef(null);

    useEffect(() => {
        const normalized = normalizeNumericValue(value);
        if (!isEditingRef.current) {
            initialValue.current = normalized;
            setInternalValue(normalized);
        }
    }, [value]);

    const commit = useCallback(() => {
        const normalized = normalizeNumericValue(internalValue);
        isEditingRef.current = false;
        if (normalized === initialValue.current) {
            return;
        }
        initialValue.current = normalized;
        if (typeof onCommit === "function") {
            onCommit(normalized === "" ? "" : normalized);
        }
    }, [internalValue, onCommit]);

    const handleValueChange = useCallback((nextValue) => {
        isEditingRef.current = true;
        if (nextValue === undefined || nextValue === null || Number.isNaN(nextValue)) {
            setInternalValue("");
            return;
        }
        setInternalValue(nextValue);
    }, []);

    const handleFocus = useCallback(() => {
        isEditingRef.current = true;
    }, []);

    const handleKeyDown = useCallback((event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            commit();
            if (inputRef.current && typeof inputRef.current.blur === "function") {
                inputRef.current.blur();
            }
        }
    }, [commit]);

    return (
        <InputFormattedNumber
            value={internalValue === "" ? "" : internalValue}
            onChange={handleValueChange}
            onBlur={commit}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            getInputRef={inputRef}
            isShowLabel={false}
            isShowPlaceholder={false}
            isFullW
            customClass={`bg-transparent border-none text-sm text-gray-900 focus:outline-none focus:ring-0 px-0${className ? ` ${className}` : ""}`}
            suffix={suffix}
            // decimalScale={decimalScale}
            allowNegative={allowNegative}
        />
    );
};

const DocumentBodyEditableFormattedNumberInput = (props) => (
    props.isEditable
        ? (
            <EditableFormattedNumber
                value={props.value}
                onCommit={props.onCommit}
                suffix={props.suffix}
                decimalScale={props.decimalScale}
                allowNegative={props.allowNegative}
                className={props.className}
            />
        )
        : (
            <ReadOnlyFormattedNumber
                value={props.value}
                suffix={props.suffix}
                decimalScale={props.decimalScale}
                className={props.className}
            />
        )
);

DocumentBodyEditableFormattedNumberInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCommit: PropTypes.func,
    suffix: PropTypes.string,
    decimalScale: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    allowNegative: PropTypes.bool,
    isEditable: PropTypes.bool,
    className: PropTypes.string,
};

DocumentBodyEditableFormattedNumberInput.defaultProps = {
    value: "",
    onCommit: undefined,
    suffix: "",
    decimalScale: false,
    allowNegative: false,
    isEditable: true,
    className: "",
};

export default DocumentBodyEditableFormattedNumberInput;
